/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import {
  $createLinkNode,
  $isAutoLinkNode,
  $isLinkNode,
  TOGGLE_LINK_COMMAND,
} from "@lexical/link";
import { $findMatchingParent, mergeRegister } from "@lexical/utils";
import {
  $getSelection,
  $isNodeSelection,
  $isRangeSelection,
  BaseSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  getDOMSelection,
  KEY_ESCAPE_COMMAND,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import * as React from "react";
import { Dispatch, useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@chakra-ui/button";
import { Input } from "@chakra-ui/input";
import { Box, Flex, HStack, Link, Spacer } from "@chakra-ui/layout";
import { BsGlobeAmericas } from "react-icons/bs";
import { RiLinkUnlink, RiPencilLine } from "react-icons/ri";
import { TbSmallIconButton } from "../../components";
import { getSelectedNode } from "../../utils/getSelectedNode";
import { setFloatingElemPositionForLinkEditor } from "../../utils/setFloatingElemPositionForLinkEditor";
import { sanitizeUrl } from "../../utils/url";
import { INSERT_LINK_COMMAND, REMOVE_LINK_COMMAND } from "./commands";

export function FloatingLinkEditor({
  editor,
  isLink,
  setIsLink,
  anchorElem,
}: {
  editor: LexicalEditor;
  isLink: boolean;
  setIsLink: Dispatch<boolean>;
  anchorElem: HTMLElement;
}) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [editedLinkUrl, setEditedLinkUrl] = useState("https://");
  const [lastSelection, setLastSelection] = useState<BaseSelection | null>(
    null
  );
  const [isLinkEditMode, setIsLinkEditMode] = useState(false);

  const $updateLinkEditor = useCallback(() => {
    const selection = $getSelection();
    let newLinkUrl = "";

    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const linkParent = $findMatchingParent(node, $isLinkNode);

      if (linkParent) {
        newLinkUrl = linkParent.getURL();
      } else if ($isLinkNode(node)) {
        newLinkUrl = node.getURL();
      }
      setLinkUrl(newLinkUrl);
      if (isLinkEditMode) {
        setEditedLinkUrl(newLinkUrl || "https://");
      }
    } else if ($isNodeSelection(selection)) {
      const nodes = selection.getNodes();
      if (nodes.length > 0) {
        const node = nodes[0];
        const parent = node?.getParent();
        if ($isLinkNode(parent)) {
          newLinkUrl = parent.getURL();
        } else if ($isLinkNode(node)) {
          newLinkUrl = node.getURL();
        }
        setLinkUrl(newLinkUrl);
        if (isLinkEditMode) {
          setEditedLinkUrl(newLinkUrl || "https://");
        }
      }
    }

    const editorElem = editorRef.current;
    const nativeSelection = getDOMSelection(editor._window);
    const activeElement = document.activeElement;
    if (editorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();

    if (selection !== null && rootElement !== null && editor.isEditable()) {
      let domRect: DOMRect | undefined;

      if ($isNodeSelection(selection)) {
        const nodes = selection.getNodes();
        if (nodes.length > 0) {
          const element = editor.getElementByKey(nodes[0]?.getKey() ?? "");
          if (element) {
            domRect = element.getBoundingClientRect();
          }
        }
      } else if (
        nativeSelection !== null &&
        rootElement.contains(nativeSelection.anchorNode)
      ) {
        domRect =
          nativeSelection.focusNode?.parentElement?.getBoundingClientRect();
      }

      if (domRect) {
        domRect.y += 100;
        setFloatingElemPositionForLinkEditor(domRect, editorElem, anchorElem);
      }
      setLastSelection(selection);
    } else if (!activeElement || activeElement.id !== "link-editor-input") {
      if (rootElement !== null) {
        setFloatingElemPositionForLinkEditor(null, editorElem, anchorElem);
      }
      setLastSelection(null);
      setIsLinkEditMode(false);
      setLinkUrl("");
    }

    return true;
  }, [anchorElem, editor, isLinkEditMode]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        $updateLinkEditor();
      });
    };

    window.addEventListener("resize", update);

    if (scrollerElem) {
      scrollerElem.addEventListener("scroll", update);
    }

    return () => {
      window.removeEventListener("resize", update);

      if (scrollerElem) {
        scrollerElem.removeEventListener("scroll", update);
      }
    };
  }, [anchorElem.parentElement, editor, $updateLinkEditor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateLinkEditor();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          editor.getEditorState().read(() => {
            $updateLinkEditor();
          });
          return true;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          if (isLink) {
            setIsLink(false);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        INSERT_LINK_COMMAND,
        () => {
          setIsLinkEditMode(true);
          editor.update(() => {
            $updateLinkEditor();
          });

          // Return true = handled, stop propagation to lower priority handlers
          return true;
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand<null>(
        REMOVE_LINK_COMMAND,
        () => {
          editor.update(() => {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
          });

          // Return true = handled, stop propagation to lower priority handlers
          return true;
        },
        COMMAND_PRIORITY_EDITOR
      )
    );
  }, [editor, $updateLinkEditor, setIsLink, isLink]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      $updateLinkEditor();
    });
  }, [editor, $updateLinkEditor]);

  useEffect(() => {
    if (isLinkEditMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLinkEditMode]);

  useEffect(() => {
    if (!isLink && !isLinkEditMode) return;

    const handleMouseDown = (event: MouseEvent) => {
      const editorElement = editorRef.current;
      if (editorElement === null) return;
      const target = event.target as Node;
      if (!editorElement.contains(target)) {
        setIsLink(false);
        setIsLinkEditMode(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown, true);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown, true);
    };
  }, [setIsLink, setIsLinkEditMode, isLink, isLinkEditMode]);

  const monitorInputInteraction = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      handleLinkSubmission(event);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setIsLinkEditMode(false);
    }
  };

  const handleLinkSubmission = (
    event: React.KeyboardEvent<HTMLInputElement> | React.MouseEvent<HTMLElement>
  ) => {
    event.preventDefault();
    if (lastSelection !== null) {
      if (editedLinkUrl !== "") {
        editor.update(() => {
          editor.dispatchCommand(
            TOGGLE_LINK_COMMAND,
            sanitizeUrl(editedLinkUrl)
          );
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const parent = getSelectedNode(selection).getParent();
            if ($isAutoLinkNode(parent)) {
              const linkNode = $createLinkNode(parent.getURL(), {
                rel: parent.__rel,
                target: parent.__target,
                title: parent.__title,
              });
              parent.replace(linkNode, true);
            }
          }
        });
      }
      setEditedLinkUrl("https://");
      setIsLinkEditMode(false);
    }
  };

  return (
    <Box
      ref={editorRef}
      display="flex"
      flexDirection="column"
      position="absolute"
      top={0}
      left={0}
      zIndex={10}
      maxW="300px"
      w="100%"
      maxH={isLinkEditMode ? "200px" : "100px"}
      overflow="hidden"
      opacity={isLinkEditMode || isLink ? 1 : 0}
      bg="white"
      boxShadow="0 5px 10px rgba(0, 0, 0, 0.3)"
      borderRadius="8px"
      transition="opacity 0.5s, max-height 0.35s ease-out"
      willChange="transform"
    >
      {isLinkEditMode ? (
        <Flex
          direction="column"
          justifyContent="space-between"
          width="100%"
          padding={3}
          gap={4}
        >
          <Input
            id="link-editor-input"
            ref={inputRef}
            value={editedLinkUrl}
            fontSize="sm"
            placeholder="Add URL"
            onChange={(event) => {
              setEditedLinkUrl(event.target.value);
            }}
            onKeyDown={(event) => {
              monitorInputInteraction(event);
            }}
          />
          <HStack justifyContent="flex-end">
            <Button
              variant="outline"
              size="sm"
              colorScheme="gray"
              onClick={() => {
                setIsLinkEditMode(false);
              }}
              aria-label="Cancel link edit"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              colorScheme="primary"
              onClick={handleLinkSubmission}
              aria-label="Confirm link"
            >
              Apply
            </Button>
          </HStack>
        </Flex>
      ) : (
        isLink && (
          <Flex
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            width="100%"
            padding={3}
            gap={2}
          >
            <BsGlobeAmericas size="20" />
            <Link
              href={sanitizeUrl(linkUrl)}
              target="_blank"
              title={linkUrl}
              fontSize="sm"
              textDecoration="underline"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              overflow="hidden"
              maxW="11.25rem"
              rel="noopener noreferrer"
            >
              {linkUrl}
            </Link>
            <Spacer />
            <TbSmallIconButton
              icon={<RiPencilLine />}
              onClick={(event) => {
                event.preventDefault();
                setIsLinkEditMode(true);
                setEditedLinkUrl(linkUrl);
              }}
              title="Edit link"
            />
            <TbSmallIconButton
              icon={<RiLinkUnlink />}
              onClick={() => {
                editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
              }}
              title="Remove link"
            />
          </Flex>
        )
      )}
    </Box>
  );
}
